import { useCallback, useEffect, useMemo, useState } from 'react';
import { adCampaignService } from '../../services/adCampaignService';
import { AdDisplayItem } from '../../types/adCampaign';
import { AD_POOL_LIMIT, AD_POOL_REFRESH_SECONDS } from './constants';

interface UseAdSlotsOptions {
    enabled?: boolean;
    limit?: number;
    refreshIntervalSeconds?: number;
}

interface UseAdSlotsResult {
    adsBySlot: Record<string, AdDisplayItem[]>;
    countsBySlot: Record<string, number>;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

const buildEmptySlots = (slots: string[]): Record<string, AdDisplayItem[]> => (
    slots.reduce<Record<string, AdDisplayItem[]>>((acc, slot) => {
        acc[slot] = [];
        return acc;
    }, {})
);

export const useAdSlots = (
    slots: readonly string[],
    {
        enabled = true,
        limit = AD_POOL_LIMIT,
        refreshIntervalSeconds = AD_POOL_REFRESH_SECONDS,
    }: UseAdSlotsOptions = {},
): UseAdSlotsResult => {
    const slotsKey = slots.join(',');
    const slotList = useMemo(() => slotsKey.split(',').filter(Boolean), [slotsKey]);
    const [adsBySlot, setAdsBySlot] = useState<Record<string, AdDisplayItem[]>>(() => buildEmptySlots(slotList));
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState<string | null>(null);

    const loadAds = useCallback(async () => {
        if (!enabled || slotList.length === 0) {
            setLoading(false);
            setAdsBySlot(buildEmptySlots(slotList));
            return;
        }

        try {
            setLoading(true);
            const response = await adCampaignService.getAdsForSlots(slotList, limit);
            const nextAds = buildEmptySlots(slotList);
            slotList.forEach((slot) => {
                nextAds[slot] = response.slots?.[slot]?.ads ?? [];
            });
            setAdsBySlot(nextAds);
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch ad slots:', err);
            setAdsBySlot(buildEmptySlots(slotList));
            setError(err?.message ?? 'Failed to fetch ads');
        } finally {
            setLoading(false);
        }
    }, [enabled, limit, slotList]);

    useEffect(() => {
        loadAds();
    }, [loadAds]);

    useEffect(() => {
        if (!enabled || refreshIntervalSeconds <= 0) return;

        const id = window.setInterval(() => {
            loadAds();
        }, refreshIntervalSeconds * 1000);

        return () => window.clearInterval(id);
    }, [enabled, loadAds, refreshIntervalSeconds]);

    const countsBySlot = useMemo(() => (
        slotList.reduce<Record<string, number>>((acc, slot) => {
            acc[slot] = adsBySlot[slot]?.length ?? 0;
            return acc;
        }, {})
    ), [adsBySlot, slotList]);

    return {
        adsBySlot,
        countsBySlot,
        loading,
        error,
        refresh: loadAds,
    };
};
